"use client";

import { Suspense, useEffect, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { login } from "@/lib/auth-api";
import { useUser } from "@/lib/user-context";

const API = "/api/v1";

function LoginForm() {
  const search = useSearchParams();
  const redirect = search.get("redirect") || "/wiki";
  const { refresh } = useUser();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      await refresh();
      // 풀 페이지 네비게이션으로 모든 컴포넌트 새로 마운트
      window.location.href = redirect;
    } catch (err) {
      setError(err instanceof Error ? err.message : "로그인 실패");
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div>
        <label
          htmlFor="signin-email"
          className="text-muted-foreground mb-1.5 block text-xs font-semibold"
        >
          이메일
        </label>
        <input
          id="signin-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          placeholder="you@example.com"
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-violet-500"
        />
      </div>

      <div>
        <label
          htmlFor="signin-password"
          className="text-muted-foreground mb-1.5 block text-xs font-semibold"
        >
          비밀번호
        </label>
        <input
          id="signin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          placeholder="••••••••"
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-violet-500"
        />
      </div>

      {error && (
        <div role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="mt-2 rounded-full bg-violet-600 py-3.5 text-base font-bold text-white shadow-lg shadow-violet-600/15 transition-all hover:-translate-y-0.5 hover:bg-violet-700 disabled:opacity-50"
      >
        {loading ? "로그인 중..." : "로그인"}
      </button>
      <p className="mt-3 text-center">
        <Link
          href="/forgot-password"
          className="text-muted-foreground text-xs hover:text-violet-600"
        >
          비밀번호를 잊으셨나요?
        </Link>
      </p>
    </form>
  );
}

function OAuthButtons() {
  const [providers, setProviders] = useState<{ kakao: boolean; naver: boolean }>({
    kakao: false,
    naver: false,
  });

  useEffect(() => {
    fetch(`${API}/auth/providers`)
      .then((r) => r.json())
      .then(setProviders)
      .catch(() => {});
  }, []);

  if (!providers.kakao && !providers.naver) return null;

  return (
    <div className="mb-6">
      <div className="flex gap-2">
        {providers.kakao && (
          <a
            href={`${API}/auth/oauth/kakao/start`}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#FEE500] py-3 text-sm font-bold text-[#3C1E1E] hover:bg-[#f5dc00]"
          >
            카카오 로그인
          </a>
        )}
        {providers.naver && (
          <a
            href={`${API}/auth/oauth/naver/start`}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#03C75A] py-3 text-sm font-bold text-white hover:bg-[#02b350]"
          >
            네이버 로그인
          </a>
        )}
      </div>
      <div className="my-5 flex items-center gap-3">
        <hr className="flex-1 border-gray-200" />
        <span className="text-muted-foreground text-xs">또는</span>
        <hr className="flex-1 border-gray-200" />
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <div className="mx-auto max-w-sm px-5 py-12">
      <h1 className="mb-2 text-center text-3xl font-extrabold tracking-tight">로그인</h1>
      <p className="text-muted-foreground mb-8 text-center text-sm">
        회원이 아니신가요?{" "}
        <Link href="/signup" className="font-semibold text-violet-600">
          회원가입
        </Link>
      </p>
      {/* 가입 혜택 */}
      <div className="gradient-primary-soft mb-6 rounded-2xl p-4">
        <p className="mb-2 text-sm font-bold text-violet-700">
          가입하면 이런 기능을 이용할 수 있어요
        </p>
        <ul className="space-y-1.5 text-xs text-violet-600">
          <li className="flex items-center gap-2">✓ 맞춤 시술 추천</li>
          <li className="flex items-center gap-2">✓ 시술 비교 저장</li>
          <li className="flex items-center gap-2">✓ AI 상담 대화 기록</li>
          <li className="flex items-center gap-2">✓ 즐겨찾기 &amp; 알림</li>
        </ul>
      </div>
      <OAuthButtons />
      <Suspense fallback={<div className="text-muted-foreground text-center text-sm">로딩...</div>}>
        <LoginForm />
      </Suspense>
      <div className="mt-6 text-center">
        <Link href="/hospital/signin" className="text-muted-foreground text-xs hover:text-blue-600">
          병원 관리자 로그인 &rarr;
        </Link>
      </div>
    </div>
  );
}
